import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import dayjs from "dayjs";

import { toast } from "sonner";
import { cn } from "@/utils/css";
import { useLoadFinderStore } from "@/state/loadFinderStore";
import { TranslatedText } from "@/components/common/text";
import { TrucksSidebar } from "@/components/dataPages/loadFinder/truck/TrucksSidebar";
import { FilterCard } from "@/components/dataPages/loadFinder/filter/FilterCard";
import { TruckFilterLoads } from "@/components/dataPages/loadFinder/loads/TruckFilterLoads";
import { CreateFilterModal } from "@/components/dataPages/loadFinder/filter/CreateFilterModal";
import { DeleteFilterConfirmModal } from "@/components/dataPages/loadFinder/filter/DeleteFilterConfirmModal";
import { LoadFinderHeader } from "@/components/dataPages/loadFinder/LoadFinderHeader";
import { StrategyPillRow } from "@/components/dataPages/loadFinder/strategy/StrategyPillRow";
import { AdjustStrategyModal } from "@/components/dataPages/loadFinder/strategy/AdjustStrategyModal";
import { TrucksCheckModal } from "@/components/dataPages/loadFinder/dailyCheckIn/TrucksCheckModal";
import { useStrategyPrefills } from "@/components/dataPages/loadFinder/hooks/useStrategyPrefills";
import { useActiveLoadsView } from "@/components/dataPages/loadFinder/hooks/useActiveLoadsView";
import { replaceLoadFinderSelection, useLoadFinderUrlSelection } from "@/components/dataPages/loadFinder/hooks/useLoadFinderUrlState";
import { useStickyCompanion } from "@/components/dataPages/loadFinder/hooks/useStickyCompanion";
import { toErrorMessage, useTruckFilterLoadsQuery } from "@/components/dataPages/loadFinder/hooks/useLoadFinderLoadsQueries";
import { useNewLoadBrowserNotifications } from "@/components/dataPages/loadFinder/hooks/useNewLoadBrowserNotifications";

// Height of the always-sticky LoadFinderHeader at the top of the right pane.
const LOAD_FINDER_HEADER_HEIGHT = 63;

// Truck-filter loads, unread loads and notification toggles are keyed by the filter's id: a truck
// can have several filters on the same lane.
const getFilterKey = (filter) => (filter ? String(filter.id) : null);

export const LoadFinder = () => {
    // Zustand store
    const {
        trucks,
        companyTrucks,
        truckFilters,
        unreadLoads,
        newLoadTimestamps,
        notificationsDisabled,
        createTruckFilterState,
        updateTruckFilterState,
        deleteTruckFilterState,
        fetchTrucks,
        fetchCompanyTrucks,
        fetchTruckFilters,
        createTruckFilter,
        deleteTruckFilter,
        updateTruckFilter,
        clearLoadFinderState,
        clearDeleteFilterError,
        markFilterLoadsAsRead,
        toggleFilterNotifications,
        clearAllNewLoadHighlights,
        refreshNewLoadTimestamps,
        dailyCheckIn,
        fetchDailyCheckInStatus,
        completeDailyCheckIn,
    } = useLoadFinderStore();

    const { truckParam, filterParam, strategyOffParam } = useLoadFinderUrlSelection();

    const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
    const [filterToDelete, setFilterToDelete] = useState(null);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [filterModalMode, setFilterModalMode] = useState("create"); // "create" | "edit"
    const [isAdjustStrategyOpen, setIsAdjustStrategyOpen] = useState(false);
    const [currentEarnMoreOn, setCurrentEarnMoreOn] = useState(2);
    const [currentMinOffer, setCurrentMinOffer] = useState(null);
    const [currentMinRpm, setCurrentMinRpm] = useState(null);
    const [currentOutboundMin, setCurrentOutboundMin] = useState(0);
    const [currentOutboundMax, setCurrentOutboundMax] = useState(3000);
    const [scrollToNewLoadTrigger, setScrollToNewLoadTrigger] = useState(0);
    const prevSearchParamsRef = useRef(null);
    const createdFilterIdRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const filtersListRef = useRef(null);
    const deselectTimeoutRef = useRef(null);
    const tableToggleRef = useRef(null);
    const [isDailyCheckOpen, setIsDailyCheckOpen] = useState(false);
    const dailyCheckHandledRef = useRef(false); // prevents reopening within the same session

    const openFilterFromNotification = useCallback(
        (filter) => {
            const filterKey = getFilterKey(filter);
            if (deselectTimeoutRef.current) {
                clearTimeout(deselectTimeoutRef.current);
                deselectTimeoutRef.current = null;
            }
            replaceLoadFinderSelection({ truckId: filter.truckId, filterId: filter.id });
            // Delayed so it runs after the truck-change reset, which clears all highlights.
            setTimeout(() => {
                clearAllNewLoadHighlights();
                refreshNewLoadTimestamps(filterKey);
                markFilterLoadsAsRead(filterKey);
                setScrollToNewLoadTrigger((prev) => prev + 1);
            }, 100);
        },
        [clearAllNewLoadHighlights, refreshNewLoadTimestamps, markFilterLoadsAsRead],
    );
    const notifyNewLoads = useNewLoadBrowserNotifications(openFilterFromNotification);
    const truckFilterLoadsQuery = useTruckFilterLoadsQuery({ onNewLoads: notifyNewLoads });
    const todayLocal = dayjs().format("YYYY-MM-DD");
    // Company trucks not pinned as "My Truck" — the wizard's second ("Other trucks") segment.
    const otherTrucks = useMemo(() => (companyTrucks.data ?? []).filter((t) => !t.isMyTruck), [companyTrucks.data]);

    useEffect(() => {
        fetchTrucks();
        fetchCompanyTrucks();
        fetchTruckFilters();
        fetchDailyCheckInStatus();
        return () => {
            clearLoadFinderState();
        };
    }, []);

    // Auto-open the daily check-in once per local day when today's check-in isn't done.
    useEffect(() => {
        if (dailyCheckHandledRef.current) return;
        if (dailyCheckIn.loading || !dailyCheckIn.fetched) return;
        if (dailyCheckIn.error) return; // don't open if we couldn't fetch status
        if (trucks.loading || truckFilters.loading) return;
        if (trucks.data === null || truckFilters.data === null) return;
        if (dailyCheckIn.lastCheckInDate === todayLocal) return;
        // Wait for company trucks to settle (loaded or errored) before deciding, so a slow
        // company fetch doesn't wrongly suppress or prematurely open the check.
        if (companyTrucks.loading) return;
        if (companyTrucks.data === null && !companyTrucks.error) return;
        // Decision is final for this session now — mark handled in BOTH branches so that adding
        // a first truck later in the session doesn't immediately re-trigger the check.
        dailyCheckHandledRef.current = true;
        // No trucks to review (neither pinned nor company) → don't auto-open.
        if (trucks.data.length + otherTrucks.length === 0) return;
        setIsDailyCheckOpen(true);
    }, [
        dailyCheckIn.loading,
        dailyCheckIn.fetched,
        dailyCheckIn.error,
        dailyCheckIn.lastCheckInDate,
        trucks.loading,
        trucks.data,
        companyTrucks.loading,
        companyTrucks.error,
        otherTrucks,
        truckFilters.loading,
        truckFilters.data,
        todayLocal,
    ]);

    // Scroll to top helper
    const scrollToTop = useCallback(() => {
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    // Create a set of truck ids that have filters
    const trucksWithFilters = new Set((truckFilters.data ?? []).map((filter) => filter.truckId).filter((id) => id != null));

    // Calculate trucks with unread loads by checking filters
    const trucksWithUnreadLoads = useMemo(() => {
        const truckIds = new Set();
        (truckFilters.data ?? []).forEach((filter) => {
            if (unreadLoads[getFilterKey(filter)]?.length > 0 && filter.truckId != null) {
                truckIds.add(filter.truckId);
            }
        });
        return truckIds;
    }, [unreadLoads, truckFilters.data]);

    // Get unread count for a specific filter
    const getUnreadCountForFilter = (filter) => {
        if (!filter) return 0;
        const filterKey = getFilterKey(filter);
        return filterKey ? (unreadLoads[filterKey]?.length ?? 0) : 0;
    };

    // Get matching-load count for a specific filter (from cached truck-filter loads).
    const getLoadCountForFilter = (filter) => {
        if (!filter) return 0;
        const filterKey = getFilterKey(filter);
        return filterKey ? (truckFilterLoadsQuery.data?.[filterKey]?.length ?? 0) : 0;
    };

    // Check if notifications are disabled for a filter
    const isNotificationsDisabled = (filter) => {
        const filterKey = getFilterKey(filter);
        return filterKey ? (notificationsDisabled[filterKey] ?? false) : false;
    };

    // Selectable trucks in sidebar order — "My trucks" first, then company trucks. `null` until both
    // lists have settled (loaded or errored), so the URL redirects below decide nothing on a
    // half-loaded list.
    const selectableTrucks = useMemo(() => {
        if (trucks.data === null && !trucks.error) return null;
        if (companyTrucks.data === null && !companyTrucks.error) return null;
        return [...(trucks.data ?? []), ...otherTrucks];
    }, [trucks.data, trucks.error, companyTrucks.data, companyTrucks.error, otherTrucks]);

    // Selection derives from the URL: ?truck=<truckId> resolved against the loaded truck lists.
    const selectedTruck = useMemo(
        () => (selectableTrucks ?? []).find((t) => String(t.truckId) === String(truckParam)) ?? null,
        [selectableTrucks, truckParam],
    );
    // Truck numbers are only unique per owner and "My trucks" can pin trucks from several owners,
    // so everything keyed to the selected truck goes by its numeric id.
    const selectedTruckId = selectedTruck?.truckId ?? null;

    // Get filters for the selected truck with unique keys
    const filtersForSelectedTruck = useMemo(
        () =>
            (truckFilters.data ?? [])
                .map((filter, index) => ({ ...filter, _filterIndex: index }))
                .filter((filter) => filter.truckId === selectedTruckId),
        [truckFilters.data, selectedTruckId],
    );

    // Selected filter derives from the URL: ?filter=<filterId>
    const selectedFilter = useMemo(
        () => (filterParam != null ? (filtersForSelectedTruck.find((p) => String(p.id) === String(filterParam)) ?? null) : null),
        [filtersForSelectedTruck, filterParam],
    );

    // - truckPrefill / globalPrefill are the raw saved scopes (un-merged); the pill row
    //   uses both to render outlined (global) vs filled (truck) chips per source.
    // - mergedTruckPrefill is the effective filter set used for fetching loads and
    //   pre-filling the new-filter modal (truck wins per-field, global fallback).
    const { truckPrefill, globalPrefill, mergedTruckPrefill, prefillReady } = useStrategyPrefills(selectedTruckId);

    // Filter selection and the strategy toggle are mutually exclusive by construction:
    // the URL holds either ?filter=<id> or ?strategy=off, never both.
    const filtersEnabled = !selectedFilter && !strategyOffParam;
    const handleToggleStrategyFilters = () => {
        if (!selectedTruck) return;
        replaceLoadFinderSelection({ truckId: selectedTruck.truckId, strategyOff: filtersEnabled });
    };

    // Companion below the truck header when scrolled: selected filter echo OR strategy pill row
    // echo. Its height drives where TruckFilterLoads' own sticky elements land. Measured live
    // (rather than a constant) because StrategyPillRow has no fixed height, so a hardcoded value
    // drifts and the loads header ends up partially hidden behind a too-tall companion.
    const companionResizeRef = useRef(null);
    const [companionHeight, setCompanionHeight] = useState(0);
    const companionRef = useCallback((el) => {
        companionResizeRef.current?.disconnect();
        if (!el) {
            setCompanionHeight(0);
            return;
        }
        const measure = () => setCompanionHeight(el.offsetHeight);
        measure();
        companionResizeRef.current = new ResizeObserver(measure);
        companionResizeRef.current.observe(el);
    }, []);
    const stickyHeaderOffset = LOAD_FINDER_HEADER_HEIGHT + companionHeight;

    const showStickyFiltersHeader = useStickyCompanion({
        scrollContainerRef,
        tableToggleRef,
        selectedTruck,
        selectedFilter,
        mergedTruckPrefill,
        filtersCount: filtersForSelectedTruck.length,
        isFiltersExpanded,
        stickyHeaderOffset,
    });

    // Reset per-truck view state when the truck changes (also covers deep links / history nav).
    useEffect(() => {
        if (deselectTimeoutRef.current) {
            clearTimeout(deselectTimeoutRef.current);
            deselectTimeoutRef.current = null;
        }
        setCurrentEarnMoreOn(2);
        setCurrentMinOffer(null);
        setCurrentMinRpm(null);
        setCurrentOutboundMin(0);
        setCurrentOutboundMax(3000);
        prevSearchParamsRef.current = null;
        createdFilterIdRef.current = null;
        clearAllNewLoadHighlights();
    }, [selectedTruckId]);

    // Cleanup deselect timeout on unmount
    useEffect(() => {
        return () => {
            if (deselectTimeoutRef.current) {
                clearTimeout(deselectTimeoutRef.current);
            }
        };
    }, []);

    const applyRequirementParams = useCallback(({ earnMoreOn, minOffer, minRpm, outboundMin, outboundMax, searchParams }) => {
        setCurrentEarnMoreOn(earnMoreOn);
        setCurrentMinOffer(minOffer);
        setCurrentMinRpm(minRpm);
        setCurrentOutboundMin(outboundMin);
        setCurrentOutboundMax(outboundMax);
        prevSearchParamsRef.current = searchParams;
    }, []);

    const { hasStrategy, requirementsLoadsQuery } = useActiveLoadsView({
        selectedTruck,
        truckPrefill,
        globalPrefill,
        mergedTruckPrefill,
        prefillReady,
        truckFiltersLoading: truckFilters.loading,
        hasFilters: filtersForSelectedTruck.length > 0,
        selectedFilter,
        filtersEnabled,
        applyRequirementParams,
    });

    // Redirect an incomplete URL to its canonical selection: pick the first truck when none is
    // set, then default the view — strategy (absent params) when the truck has one, otherwise
    // the truck's top filter. ?strategy=off and a resolved ?filter are explicit states and win.
    useEffect(() => {
        if (selectableTrucks === null) return;

        if (!selectedTruck) {
            if (selectableTrucks.length > 0) {
                replaceLoadFinderSelection({ truckId: selectableTrucks[0].truckId });
            }
            return;
        }

        if (strategyOffParam || selectedFilter) return;
        if (truckFilters.data === null || !prefillReady) return;

        if (hasStrategy) {
            // Absent params == strategy view; only clean up a stale ?filter that didn't resolve.
            if (filterParam != null) replaceLoadFinderSelection({ truckId: selectedTruck.truckId });
            return;
        }
        if (filtersForSelectedTruck.length > 0) {
            replaceLoadFinderSelection({ truckId: selectedTruck.truckId, filterId: filtersForSelectedTruck[0].id });
            return;
        }
        if (filterParam != null) replaceLoadFinderSelection({ truckId: selectedTruck.truckId });
    }, [
        selectableTrucks,
        selectedTruck,
        selectedFilter,
        strategyOffParam,
        filterParam,
        truckFilters.data,
        prefillReady,
        hasStrategy,
        filtersForSelectedTruck,
    ]);

    const strategyActive = hasStrategy && filtersEnabled;
    const requirementsLoading = (selectedTruckId != null && !prefillReady) || requirementsLoadsQuery.isLoading;

    // Select the newly created filter once the refreshed list contains it. Looked up by the id the
    // create returned, since the truck may already have a filter on the same lane.
    useEffect(() => {
        const createdFilterId = createdFilterIdRef.current;
        if (createdFilterId == null || truckFilters.loading || !selectedTruck) return;
        const createdFilter = filtersForSelectedTruck.find((filter) => String(filter.id) === String(createdFilterId));
        if (!createdFilter) return;
        createdFilterIdRef.current = null;
        replaceLoadFinderSelection({ truckId: selectedTruck.truckId, filterId: createdFilter.id });
        // Scroll to top for the new filter
        scrollToTop();
        truckFilterLoadsQuery.refresh();
    }, [filtersForSelectedTruck, truckFilters.loading]);

    // Get loads for the selected filter (API returns EnrichedLoadBoardItem keyed by the filter's id)
    const loadsForSelectedFilter = useMemo(() => {
        if (!selectedFilter || !truckFilterLoadsQuery.data) return [];
        return truckFilterLoadsQuery.data[getFilterKey(selectedFilter)] ?? [];
    }, [selectedFilter, truckFilterLoadsQuery.data]);

    // Get earnMoreOn from selected filter (for CM score calculation, default index is 2)
    const filterEarnMoreOn = selectedFilter?.earnMoreOn ?? 2;

    // Filter requirements loads based on minOffer, minRpm, and outbound distance
    const filteredRequirementsLoads = useMemo(() => {
        if (!requirementsLoadsQuery.data) return null;

        const norm = (s) => (s == null ? "" : String(s).trim().toLowerCase());
        const prefill = strategyActive ? mergedTruckPrefill : null;
        const preferredBrokers = new Set((prefill?.preferredBrokers ?? []).map(norm).filter(Boolean));
        const avoidedBrokers = new Set((prefill?.avoidedBrokers ?? []).map(norm).filter(Boolean));
        const avoidedMarkets = new Set((prefill?.avoidedMarkets ?? []).map(norm).filter(Boolean));

        const filteredOut = [];
        const result = requirementsLoadsQuery.data.filter((load) => {
            const loadId = load.id ?? load.loadId ?? "unknown";
            // Filter by outbound distance
            if (load.loadDistance != null) {
                if (load.loadDistance < currentOutboundMin || load.loadDistance > currentOutboundMax) {
                    filteredOut.push({
                        loadId,
                        reason: `outbound distance ${load.loadDistance} outside [${currentOutboundMin}, ${currentOutboundMax}]`,
                    });
                    return false;
                }
            }
            // Filter by min offer - exclude loads without offer when filter is active
            if (currentMinOffer != null) {
                if (load.offerRate == null || load.offerRate < currentMinOffer) {
                    filteredOut.push({
                        loadId,
                        reason: `offerRate ${load.offerRate ?? "null"} below minOffer ${currentMinOffer}`,
                    });
                    return false;
                }
            }
            // Filter by min RPM - exclude loads without offer/distance when filter is active
            if (currentMinRpm != null) {
                if (!load.loadDistance || !load.offerRate) {
                    filteredOut.push({
                        loadId,
                        reason: `missing distance/offer for minRpm ${currentMinRpm} (distance=${load.loadDistance ?? "null"}, offer=${load.offerRate ?? "null"})`,
                    });
                    return false;
                }
                const rpm = load.offerRate / load.loadDistance;
                if (rpm < currentMinRpm) {
                    filteredOut.push({
                        loadId,
                        reason: `rpm ${rpm.toFixed(2)} below minRpm ${currentMinRpm}`,
                    });
                    return false;
                }
            }
            // Broker preferences
            if (preferredBrokers.size || avoidedBrokers.size) {
                const broker = norm(load.brokerInfo?.name);
                if (avoidedBrokers.has(broker)) {
                    filteredOut.push({ loadId, reason: `broker "${broker}" is avoided` });
                    return false;
                }
                if (preferredBrokers.size && !preferredBrokers.has(broker)) {
                    filteredOut.push({ loadId, reason: `broker "${broker}" not in preferred brokers` });
                    return false;
                }
            }
            // Avoided destination markets (last itinerary stop's KMA market)
            if (avoidedMarkets.size) {
                const stops = load.itineraryStops;
                const destMarket = norm(stops?.[stops.length - 1]?.marketName);
                if (destMarket && avoidedMarkets.has(destMarket)) {
                    filteredOut.push({ loadId, reason: `destination market "${destMarket}" is avoided` });
                    return false;
                }
            }
            return true;
        });

        if (filteredOut.length) {
            console.log(
                `[LoadFinder] Filtered out ${filteredOut.length} of ${requirementsLoadsQuery.data.length} requirements loads:`,
                filteredOut,
            );
        }
        return result;
    }, [
        requirementsLoadsQuery.data,
        currentMinOffer,
        currentMinRpm,
        currentOutboundMin,
        currentOutboundMax,
        strategyActive,
        mergedTruckPrefill,
    ]);

    // Toggle filter selection (single selection only)
    const toggleFilterSelection = (filter) => {
        if (!selectedTruck || !filter) return;
        // Clear any pending deselect timeout
        if (deselectTimeoutRef.current) {
            clearTimeout(deselectTimeoutRef.current);
            deselectTimeoutRef.current = null;
        }

        // Switching filter is a context change — clear all existing green highlights.
        // The newly-selected filter re-greens its own unread loads below.
        clearAllNewLoadHighlights();

        if (selectedFilter?.id === filter.id) {
            // Deselect if clicking the same filter — explicit "all loads" view (radio off).
            replaceLoadFinderSelection({ truckId: selectedTruck.truckId, strategyOff: true });
            // After 800ms, scroll to top and expand filters list
            deselectTimeoutRef.current = setTimeout(() => {
                setIsFiltersExpanded(true);
                scrollToTop();
            }, 800);
        } else {
            // Select the new filter
            replaceLoadFinderSelection({ truckId: selectedTruck.truckId, filterId: filter.id });
            // Check if this filter has unread loads
            const filterKey = getFilterKey(filter);
            const hasUnreadLoads = filterKey ? (unreadLoads[filterKey]?.length ?? 0) > 0 : false;

            if (hasUnreadLoads) {
                // Refresh timestamps so loads are highlighted green (in case original timestamps expired)
                refreshNewLoadTimestamps(filterKey);
                // Don't scroll to top - we'll scroll to the new load instead
                // Trigger scroll to first new load (with delay to allow data and virtualizer to be ready)
                setTimeout(() => {
                    setScrollToNewLoadTrigger((prev) => prev + 1);
                }, 100);
            } else {
                // No unread loads - scroll to top as normal
                scrollToTop();
            }

            if (filterKey) {
                markFilterLoadsAsRead(filterKey);
            }
        }
    };

    // Handle truck card click
    const handleTruckCardClick = (truckId) => {
        const truck = (selectableTrucks ?? []).find((t) => t.truckId === truckId);
        if (!truck) return;
        replaceLoadFinderSelection({ truckId: truck.truckId });
        // Don't mark filters as read here - let the user see the notifications
        // Filters will be marked as read when the user clicks on a specific filter
    };

    // Handle toggle notifications for a filter
    const handleToggleNotifications = (filter) => {
        const filterKey = getFilterKey(filter);
        if (filterKey) {
            toggleFilterNotifications(filterKey);
        }
    };

    const handleDeleteFilter = async () => {
        if (!filterToDelete) return;
        try {
            await deleteTruckFilter(filterToDelete.id);
            toast.success("Filter has been deleted", { className: "cm-toast-success", toasterId: "top-right" });
            setFilterToDelete(null);
            // Clear selection if the deleted filter was selected
            if (selectedFilter?.id === filterToDelete.id && selectedTruck) {
                replaceLoadFinderSelection({ truckId: selectedTruck.truckId, strategyOff: true });
            }
            // Refresh truck filters list
            fetchTruckFilters();
        } catch {
            toast.error("Failed to delete filter", { className: "cm-toast-error", toasterId: "top-right" });
        }
    };

    const handleNewFilter = () => {
        setFilterModalMode("create");
        setIsFilterModalOpen(true);
    };

    const handleEditFilter = (filter) => {
        setFilterModalMode("edit");
        if (selectedTruck) {
            replaceLoadFinderSelection({ truckId: selectedTruck.truckId, filterId: filter.id });
        }
        setIsFilterModalOpen(true);
    };

    const handleCloseFilterModal = () => {
        setIsFilterModalOpen(false);
    };

    const handleSubmitFilterModal = async (payload, { isEdit }) => {
        if (!payload) return;
        try {
            if (isEdit) {
                if (!selectedFilter) return;
                await updateTruckFilter(selectedFilter.id, payload);
                toast.success("Filter has been updated!", { className: "cm-toast-success", toasterId: "top-right" });
                fetchTruckFilters();
                truckFilterLoadsQuery.refresh({ skipNotifications: true });
            } else {
                const result = await createTruckFilter(payload);
                toast.success(`Filter for ${selectedTruck?.truckName} has been successfully created!`, {
                    className: "cm-toast-success",
                    toasterId: "top-right",
                });
                createdFilterIdRef.current = result?.successfulPostings?.[0]?.id ?? null;
                fetchTruckFilters();
            }
            setIsFilterModalOpen(false);
        } catch {
            toast.error(isEdit ? "Failed to update filter" : "Failed to create filter", {
                className: "cm-toast-error",
                toasterId: "top-right",
            });
        }
    };

    const handleCompleteDailyCheck = async () => {
        try {
            await completeDailyCheckIn(todayLocal);
            setIsDailyCheckOpen(false);
        } catch (err) {
            toast.error("Failed to save daily check-in", { className: "cm-toast-error", toasterId: "top-right" });
            throw err; // let the modal know stamping failed so it stays open and skips summaries
        }
    };

    const handleRunCheck = () => setIsDailyCheckOpen(true);

    const handleAdjustStrategy = () => setIsAdjustStrategyOpen(true);

    return (
        <div className="flex w-full flex-1 border-t border-cm-p-outline">
            <TrucksSidebar
                trucks={trucks}
                companyTrucks={companyTrucks}
                selectedTruckId={selectedTruckId}
                trucksWithFilters={trucksWithFilters}
                trucksWithUnreadLoads={trucksWithUnreadLoads}
                onSelectTruck={handleTruckCardClick}
                onRunCheck={handleRunCheck}
            />

            <div className="flex-1 relative">
                <div ref={scrollContainerRef} className="absolute inset-0 bg-cm-p-background overflow-y-auto flex flex-col isolate">
                    {/* Top header - always pinned */}
                    <div className="sticky top-0 left-0 right-0 z-40">
                        <LoadFinderHeader
                            selectedTruck={selectedTruck}
                            filterCount={filtersForSelectedTruck.length}
                            onAdjustStrategy={handleAdjustStrategy}
                            onNewFilter={handleNewFilter}
                        />
                    </div>

                    {/* Strategy pill row (natural position) - shown when selected truck has a saved strategy.
                        When there are no filters the loads header follows directly, so add a bottom gap to
                        match the spacing the filters card's mt-3 provides in the with-filters case. This is plain
                        scroll content, so it doesn't affect sticky offsets or the virtualizer's measured margin. */}
                    {selectedTruck && mergedTruckPrefill && (
                        <div className={cn("px-4 pt-3", { "pb-3": filtersForSelectedTruck.length === 0 })}>
                            <StrategyPillRow
                                truckPrefill={truckPrefill}
                                globalPrefill={globalPrefill}
                                scopeLabel="Driver's strategy"
                                filtersEnabled={filtersEnabled}
                                onToggleFilters={handleToggleStrategyFilters}
                            />
                        </div>
                    )}

                    {/* Sticky companion - selected filter echo, appears when loads section approaches */}
                    {selectedTruck && filtersForSelectedTruck.length > 0 && selectedFilter && (
                        <div
                            ref={companionRef}
                            className={`${showStickyFiltersHeader ? "sticky" : "absolute pointer-events-none opacity-0"} left-0 right-0 z-30 bg-cm-p-background`}
                            style={
                                showStickyFiltersHeader
                                    ? { top: LOAD_FINDER_HEADER_HEIGHT, marginBottom: `-${companionHeight}px` }
                                    : { top: LOAD_FINDER_HEADER_HEIGHT }
                            }
                        >
                            <div className="pl-[29px] pr-5 py-1">
                                <FilterCard
                                    filter={selectedFilter}
                                    selected
                                    onToggleSelect={() => toggleFilterSelection(selectedFilter)}
                                    onDelete={setFilterToDelete}
                                    onEdit={handleEditFilter}
                                    unreadCount={getUnreadCountForFilter(selectedFilter)}
                                    notificationsDisabled={isNotificationsDisabled(selectedFilter)}
                                    onToggleNotifications={() => handleToggleNotifications(selectedFilter)}
                                    loadCount={getLoadCountForFilter(selectedFilter)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Sticky companion - strategy pill row, appears when no filter is selected and a strategy exists */}
                    {selectedTruck && !selectedFilter && mergedTruckPrefill && (
                        <div
                            ref={companionRef}
                            className={`${showStickyFiltersHeader ? "sticky" : "absolute pointer-events-none opacity-0"} left-0 right-0 z-30 bg-cm-p-background`}
                            style={
                                showStickyFiltersHeader
                                    ? { top: LOAD_FINDER_HEADER_HEIGHT, marginBottom: `-${companionHeight}px` }
                                    : { top: LOAD_FINDER_HEADER_HEIGHT }
                            }
                        >
                            <div className="px-4 py-2">
                                <StrategyPillRow
                                    truckPrefill={truckPrefill}
                                    globalPrefill={globalPrefill}
                                    scopeLabel="Driver's strategy"
                                    filtersEnabled={filtersEnabled}
                                    onToggleFilters={handleToggleStrategyFilters}
                                    bordered={false}
                                />
                            </div>
                        </div>
                    )}

                    {/* Filters section: bordered card with label + filter rows */}
                    {selectedTruck && filtersForSelectedTruck.length > 0 && (
                        <div className="flex-1 flex flex-col min-h-0">
                            <div
                                ref={filtersListRef}
                                className="mx-4 mt-3 mb-4 border border-cm-p-outline rounded-[8px] pl-3 pr-2 py-2 flex flex-col gap-1"
                            >
                                <span className="text-[12px] font-normal text-cm-p-text-secondary">
                                    <TranslatedText>Filters</TranslatedText>
                                </span>
                                <div className="flex flex-col w-full">
                                    {filtersForSelectedTruck.map((filter) => (
                                        <FilterCard
                                            key={filter._filterIndex}
                                            filter={filter}
                                            selected={selectedFilter?.id === filter.id}
                                            onToggleSelect={() => toggleFilterSelection(filter)}
                                            onDelete={setFilterToDelete}
                                            onEdit={handleEditFilter}
                                            unreadCount={getUnreadCountForFilter(filter)}
                                            notificationsDisabled={isNotificationsDisabled(filter)}
                                            onToggleNotifications={() => handleToggleNotifications(filter)}
                                            loadCount={getLoadCountForFilter(filter)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Loads for selected filter */}
                            {selectedFilter ? (
                                <TruckFilterLoads
                                    loads={loadsForSelectedFilter}
                                    loading={truckFilterLoadsQuery.isLoading}
                                    error={toErrorMessage(truckFilterLoadsQuery.error)}
                                    hideLoadId
                                    hideTruck
                                    headerVariant="active"
                                    averageEarnMoreOn={filterEarnMoreOn}
                                    newLoadTimestamps={newLoadTimestamps}
                                    scrollToNewLoadTrigger={scrollToNewLoadTrigger}
                                    stickyHeaderOffset={stickyHeaderOffset}
                                    scrollContainerRef={scrollContainerRef}
                                    tableToggleRef={tableToggleRef}
                                />
                            ) : (
                                /* Loads when no filter is selected */
                                <TruckFilterLoads
                                    loads={filteredRequirementsLoads}
                                    loading={requirementsLoading}
                                    searching={requirementsLoading}
                                    error={toErrorMessage(requirementsLoadsQuery.error)}
                                    hideLoadId
                                    hideTruck
                                    headerVariant={strategyActive ? "strategy" : "all"}
                                    averageEarnMoreOn={currentEarnMoreOn}
                                    defaultSortKey="cmScore"
                                    stickyHeaderOffset={stickyHeaderOffset}
                                    scrollContainerRef={scrollContainerRef}
                                    tableToggleRef={tableToggleRef}
                                />
                            )}
                        </div>
                    )}

                    {/* Loads when truck selected but no filters: strategy view if a strategy
                        is active, otherwise the matching ("no-filter") view from truck data. */}
                    {selectedTruck && filtersForSelectedTruck.length === 0 && !truckFilters.loading && (
                        <TruckFilterLoads
                            loads={filteredRequirementsLoads}
                            loading={requirementsLoading}
                            searching={requirementsLoading}
                            error={toErrorMessage(requirementsLoadsQuery.error)}
                            hideLoadId
                            hideTruck
                            headerVariant={strategyActive ? "strategy" : "no-filter"}
                            averageEarnMoreOn={currentEarnMoreOn}
                            defaultSortKey="cmScore"
                            stickyHeaderOffset={LOAD_FINDER_HEADER_HEIGHT}
                            scrollContainerRef={scrollContainerRef}
                        />
                    )}
                </div>
            </div>

            <DeleteFilterConfirmModal
                isOpen={!!filterToDelete}
                onClose={() => {
                    setFilterToDelete(null);
                    clearDeleteFilterError();
                }}
                onConfirm={handleDeleteFilter}
                filter={filterToDelete}
                loading={deleteTruckFilterState.loading}
                error={deleteTruckFilterState.error}
            />
            <AdjustStrategyModal
                isOpen={isAdjustStrategyOpen}
                selectedTruck={selectedTruck}
                truckId={selectedTruckId}
                onClose={() => setIsAdjustStrategyOpen(false)}
            />
            <CreateFilterModal
                isOpen={isFilterModalOpen}
                mode={filterModalMode}
                selectedTruck={selectedTruck}
                selectedFilter={selectedFilter}
                prefill={mergedTruckPrefill}
                onClose={handleCloseFilterModal}
                onSubmit={handleSubmitFilterModal}
                loading={createTruckFilterState.loading || updateTruckFilterState.loading}
                error={createTruckFilterState.error || updateTruckFilterState.error}
            />
            <TrucksCheckModal
                isOpen={isDailyCheckOpen}
                myTrucks={trucks.data ?? []}
                otherTrucks={otherTrucks}
                truckFilters={truckFilters.data ?? []}
                onClose={handleCompleteDailyCheck}
            />
        </div>
    );
};
